<template>
  <NsTextField
    :id="id"
    v-model="currency"
    :clearable="clearable"
    :disabled="disabled"
    :icon="icon"
    :label="label"
    :name="name"
    :placeholder="placeholder"
    :type="type"
    :value="value"
    v-on="$listeners"
    @keydown="handleKeydown"
    @focus="handleFocus"
  >
    <slot>
      {{ $slots.default }}
    </slot>
  </NsTextField>
</template>

<script>
import { NsTextField } from "@nuskin/design-components";
const allowedKeys = [9, 27, 37, 38, 39, 40];

/**
 * - Full selection on focus
 * - Type before “.” will add numbers
 * - Type after “.” will replace numbers
 * - Backspace before “.” will remove numbers, and moves cursor left
 * - Backspace after “.” will replace number with “0” and moves cursor left
 */

export default {
  name: "NsCurrencyTextField",
  components: {
    NsTextField
  },
  props: {
    id: {
      type: String,
      default: `id${Math.round(Math.random() * 1000000)}`
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: "text"
    },
    value: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      currency: this.value
    };
  },
  watch: {
    value(val) {
      this.currency = val;
    },
    currency(value, prevValue) {
      const el = document.getElementById(this.id);
      const cursorPos = el.selectionStart;
      const newValue = this.toFixedNoRounding(+value, 2);
      const newPrevValue = this.toFixedNoRounding(+prevValue, 2);

      if (newPrevValue !== newValue) {
        this.$emit("input", newValue);
      }

      if (value !== newValue) {
        this.currency = newValue;
        this.$nextTick(() => {
          this.setCaretToPos(el, cursorPos);
        });
      }
    }
  },
  mounted() {
    const el = document.getElementById(this.id);
    if (el) {
      el.setAttribute("autocomplete", "off");
    }
  },
  methods: {
    handleKeydown(event) {
      // don't allow modifiers
      if (event.ctrlKey || event.shiftKey || event.altKey) {
        event.preventDefault();
      }

      // if decimal
      if (event.key === "." || event.keyCode === 190) {
        event.preventDefault();
        const cursorPos = this.getCaretPos();
        const value = event.target.value;
        const decIndex = value.indexOf(".");
        const numbers = value.replace(".", "");

        // if cursor is after decimal
        if (cursorPos > decIndex) {
          const newValue =
            numbers.substring(0, cursorPos - 1) +
            "." +
            numbers.substring(cursorPos - 1);
          this.currency = newValue;
          this.$nextTick(() => {
            this.setCaretToPos(event.target, cursorPos + 1);
          });
          // if cursor is at decimal
        } else if (cursorPos === decIndex) {
          this.$nextTick(() => {
            this.setCaretToPos(event.target, cursorPos + 1);
          });
          // if cursor is before decimal
        } else if (cursorPos < decIndex) {
          const newValue =
            numbers.substring(0, cursorPos) +
            "." +
            numbers.substring(cursorPos);
          this.setCaretToPos(event.target, cursorPos + 1);
          this.currency = newValue;
        }

        // if number
      } else if (event.keyCode >= 48 && event.keyCode <= 57) {
        const value = event.target.value;
        const decIndex = value.indexOf(".");
        const cursorPos = this.getCaretPos();

        // if cursor right of decimal replace, else do normal
        if (decIndex > -1 && cursorPos > decIndex) {
          event.preventDefault();
          const newValue = `${value.substring(0, cursorPos)}${
            event.key
          }${value.substring(cursorPos + 1)}`;
          this.currency = newValue;
          this.$nextTick(() => {
            this.setCaretToPos(event.target, cursorPos + 1);
          });
        }
      } else if (event.key === "Backspace" || event.keyCode === "8") {
        const value = event.target.value;
        const decIndex = value.indexOf(".");
        const cursorPos = this.getCaretPos();

        // if cursor is right of decimal delete ones digit
        if (cursorPos === decIndex + 1) {
          event.preventDefault();
          const newValue = `${value.substring(
            0,
            cursorPos - 2
          )}${value.substring(cursorPos - 1)}`;
          this.currency = newValue;
          this.$nextTick(() => {
            this.setCaretToPos(event.target, cursorPos - 2);
          });
          // if cursor right of decimal replace with 0, else do normal
        } else if (cursorPos > decIndex) {
          event.preventDefault();
          const newValue = `${value.substring(
            0,
            cursorPos - 1
          )}0${value.substring(cursorPos)}`;
          this.currency = newValue;
          this.$nextTick(() => {
            this.setCaretToPos(event.target, cursorPos - 1);
          });
        }
      } else if (!allowedKeys.includes(event.keyCode)) {
        event.preventDefault();
      }
    },
    handleFocus(event) {
      const inputEl = event.target;

      if (inputEl) {
        inputEl.select();
      }
    },
    getCaretPos() {
      const el = document.getElementById(this.id);
      return el.selectionStart;
    },
    setCaretToPos(input, pos) {
      this.setSelectionRange(input, pos, pos);
    },
    setSelectionRange(input, selectionStart, selectionEnd) {
      if (input.setSelectionRange) {
        input.focus();
        input.setSelectionRange(selectionStart, selectionEnd);
      } else if (input.createTextRange) {
        var range = input.createTextRange();
        range.collapse(true);
        range.moveEnd("character", selectionEnd);
        range.moveStart("character", selectionStart);
        range.select();
      }
    },
    toFixedNoRounding(value, n) {
      const reg = new RegExp("^-?\\d+(?:\\.\\d{0," + n + "})?", "g");
      const a = value.toString().match(reg)[0];
      const dot = a.indexOf(".");
      if (dot === -1) {
        // integer, insert decimal dot and pad up zeros
        return a + "." + "0".repeat(n);
      }
      const b = n - (a.length - dot) + 1;
      return b > 0 ? a + "0".repeat(b) : a;
    }
  }
};
</script>

<style lang="scss" scoped></style>
